import reduxStore from "../configureEnvStore";
import moment from "moment";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../constants/PublicVariables";
import { ENABLED, FULL_DATE_FORMAT } from "../constants/Constants";
import { addOrUpdateCaption, failedLiveCaptionUpload, startLiveCaptionUpload, setCaptionTextOn } from "../reducers/liveCaption-reducer";
import Localize from "../locale/Localize";
import { getParticipantIdentity, getParticipantIndex } from "../shared/dataTrackFunction";
import { HOST_NAME } from "../constants/LiveCaptionConstants";
import api from "../apis/api";
import { getActiveRoom } from "../components/Twilio/twilioCallUtility";
import { isAgentIdentity, isSilentObserver } from "../shared/utility";
import { callTranscriptAPIForAISummary } from "../actions/liveCaptionAction";
import { LogFirebase } from "./firestore";

class LiveCaptionService {
    timerValue = null;
    /** Format display name/title of live caption */
    allLiveCaptionUserNameWithFormat = (isAgent, name = "", partiId) => {
        const langCode = PublicVariables.currentLanguageObj.languageCode;
        const youLabel = Localize[langCode]["you"];
        const formattedYouLabel = youLabel?.substring(1, youLabel.length - 1); // safer than substr
        const partiLabel = Localize[langCode]["participant"];
        const partiRegex = new RegExp(`^${partiLabel}\\s+`, "i");

        const isLocalUser = CALL_DATA.localIdentity === partiId;

        // --- Helper: Replace "Participant 1" -> "P1"
        const getGuestName = (participantName) => participantName.replace(partiRegex, "P");

        // --- Helper: Handle silent observer
        const getSilentObserverViewName = () => {
            const observerName = partiRegex.test(name) ? "" : name;
            const callParticipants = reduxStore.getState().commonReducer.callParticipants;
            const userData = callParticipants.find((user) => user.identity === partiId);
            const index = userData?.isSilentObserverindex > 1 ? ` ${userData?.isSilentObserverindex}` : "";

            if (!observerName && PublicVariables.isMobile) {
                const mobileIndex = userData?.isSilentObserverindex > 1 ? `${userData?.isSilentObserverindex}` : "1";
                return `O${mobileIndex}`;
            }
            const finalName = this.formatObserverName(observerName, index);
            return this.getGuestDisplayNameMweb(finalName);
        };

        // --- Return Conditions
        if (isLocalUser) return formattedYouLabel; // You
        if (isAgent) return HOST_NAME; // Host
        if (isSilentObserver(partiId)) return getSilentObserverViewName();

        // --- Default Guest Name
        const guestName = partiRegex.test(name) && PublicVariables.isMobile ? getGuestName(name) : name;
        return this.getGuestDisplayNameMweb(guestName);
    };

    /** Display guest name in MWeb - show first word of name, truncated to 10 characters if necessary */
    getGuestDisplayNameMweb = (name = "") => {
        if (!name) return "";
        const limit = PublicVariables.isMobile ? 10 : 15; // Mobile : 10 characters Desktop : 15 characters
        const parts = name.trim().split(" ");
        const first = parts[0] || "";
        return name.length <= limit ? name : first.slice(0, limit);
    };

    storeTranscriptionDataServiceFunction = async (req) => {
        return api.liveCaption.storeTranscriptionData(req);
    };

    formatObserverName = (name, index = "") => {
        const localisedValues = Localize[PublicVariables.currentLanguageObj?.languageCode];
        const observerLabel = localisedValues?.observer_lable;
        if (!name) return `${observerLabel}${index}`;
        return `${name} (${observerLabel}${index})`;
    };

    getSilentObserverName = (participantID, name) => {
        const partiLabel = Localize[PublicVariables.currentLanguageObj.languageCode]?.["participant"];
        const partiRegex = new RegExp(`^${partiLabel}\\s+`, "i"); // Regex with localized participant label
        const observerName = partiRegex.test(name) ? "" : name;
        const callParticipants = reduxStore.getState().commonReducer.callParticipants;
        const userData = callParticipants.find((user) => user.identity === participantID);
        const index = userData?.isSilentObserverindex > 1 ? ` ${userData?.isSilentObserverindex}` : "";
        return this.formatObserverName(observerName, index);
    };

    async prepareTranscriptForCallSummary() {
        const liveCaptionState = reduxStore.getState().liveCaption;
        // 🔹 Exit early if:
        //   - an upload is already in progress, OR
        //   - no captions are available to upload
        if (liveCaptionState.startLiveCaptionUploading || liveCaptionState.liveCaptionBuffer.length === 0) return;

        // 🔹 Dispatch action: move captions from liveCaptionBuffer → pendingLiveCaptionUploadData
        reduxStore.dispatch(startLiveCaptionUpload());

        try {
            // 🔹 Fetch the pending captions to be uploaded
            const liveCaptionData = reduxStore.getState().liveCaption.pendingLiveCaptionUploadData;
            console.log("pendingLiveCaptionUploadData", liveCaptionData);

            // 🔹 Clean up payload before sending to API
            //    - Remove unnecessary keys (isFinal, partial_results, name)
            //    - Add localized participantName (handle silent observers separately)
            const cleanedPayload = liveCaptionData.map(({ isFinal, partial_results, participantID, name, ...rest }) => {
                // Check if this participant is a silent observer
                return {
                    ...rest,
                    participantID,
                    name: isSilentObserver(participantID) ? this.getSilentObserverName(participantID, name) : name,
                };
            });
            LogFirebase("LiveCaption", "callingLivecaptionStoreAPI", "true");
            if (cleanedPayload) {
                LogFirebase("LiveCaption", "API_CALL_after_partial_results_false", cleanedPayload.length);
            }
            // 🔹 Dispatch API call for transcript summary
            reduxStore.dispatch(callTranscriptAPIForAISummary(cleanedPayload));

            // 🔹 Debug log of final payload sent
            console.log("CaptionStoreProcess__cleanedPayload", cleanedPayload);
        } catch (error) {
            // 🔹 Log error and rollback captions:
            //    - Move pending captions back into liveCaptionBuffer
            console.error("❌ Failed to post captions:", error);
            LogFirebase("LiveCaption", "callingLivecaptionStoreAPI_Error", error || "true");
            reduxStore.dispatch(failedLiveCaptionUpload());
        }
    }

    checkTranscriptForSummary = (isFromEndCall = false) => {
        if (reduxStore.getState().liveCaption.liveCaptionBuffer.length === 0) return;
        if (
            GLOBAL_MEETING_DATA.isAgent &&
            PublicVariables.featureFlag?.SHOMEAICALLSUMMARY === ENABLED &&
            (reduxStore.getState().liveCaption.liveCaptionBuffer.length >= 5 || isFromEndCall)
        ) {
            LogFirebase("LiveCaption", "checkLiveCaptionBuffer", {
                isFromEndCall: isFromEndCall,
                liveCaptionBuffer_length: reduxStore.getState().liveCaption?.liveCaptionBuffer?.length,
            });
            this.prepareTranscriptForCallSummary(); // call the API
        }
    };

    getCallDurationInSecond = (timeStr) => {
        const parts = timeStr.split(":").map(Number);

        if (parts.length === 2) {
            const [mm, ss] = parts;
            return mm * 60 + ss;
        } else if (parts.length === 3) {
            const [hh, mm, ss] = parts;
            return hh * 3600 + mm * 60 + ss;
        }
    };

    /** live caption reset */
    resetLiveCaption = () => {
        clearTimeout(this.timerValue);
        this.timerValue = null;
    };

    liveCaptionModalShowHide = () => {
        clearTimeout(this.timerValue);
        this.timerValue = setTimeout(() => {
            reduxStore.dispatch(setCaptionTextOn(false));
        }, 10000);
    };

    addCaption(transcriptionEvent) {
        reduxStore.dispatch(setCaptionTextOn(true));
        this.liveCaptionModalShowHide();
        const room = getActiveRoom();
        const participantID = getParticipantIdentity(room, transcriptionEvent.participant);
        if (!transcriptionEvent.partial_results) {
            LogFirebase("LiveCaption", "sentance_partial_results_false", {
                partial_results: transcriptionEvent.partial_results,
                participantId: participantID,
            });
        }
        const isSilentObserverIdentity = isSilentObserver(participantID);
        if (isSilentObserverIdentity && CALL_DATA.localIdentity !== participantID && (GLOBAL_MEETING_DATA.isGuest || CALL_DATA.isObserver)) return;
        const isAgent = isAgentIdentity(participantID);
        const participantName = getParticipantIndex(participantID);
        const callDuration = this.getCallDurationInSecond(reduxStore.getState().commonReducer.callDuration);
        const createTime = moment.utc(transcriptionEvent?.timestamp, FULL_DATE_FORMAT)?.local()?.format("hh:mm A");
        const epochTimeStamp = Math.floor(Date.now() / 1000);

        const data = {
            ...transcriptionEvent,
            participantName,
            participantID,
            isAgent,
            createTime,
            callDuration,
            epochTimeStamp,
        };
        console.log(`addCaption - ${participantName}: ${data}`);
        reduxStore.dispatch(addOrUpdateCaption(data));
        this.checkTranscriptForSummary(); // handling AI transcription summary Call data
    }
}
export default new LiveCaptionService();
